import type { TUuid } from '../types/common';
import type { TFacilityHierarchyRef } from '../types/facility';
import type { TFacilityTreeNode, TWithChildren } from '../types/facilityTree';
import { refId } from './common';

/**
 * Groups a flat facility list into a parent-id → children map.
 *
 * Facilities without a parent are omitted (they are roots, not children).
 * Children keep the input order; callers that need a specific sibling order
 * sort the arrays themselves.
 *
 * Shared by expandFacilitySubtree, sortFacilityIdsDFS, and the distribute
 * planning-group handler so the by-parent_facility construction lives once.
 */
export const buildFacilityChildrenMap = <T extends TFacilityHierarchyRef>(
  facilities: ReadonlyArray<T>,
): Map<TUuid, T[]> => {
  const childrenByParent = new Map<TUuid, T[]>();
  facilities.forEach((facility) => {
    const parent = refId(facility.parent_facility);
    if (!parent) return;
    const siblings = childrenByParent.get(parent);
    if (siblings) {
      siblings.push(facility);
    } else {
      childrenByParent.set(parent, [facility]);
    }
  });
  return childrenByParent;
};

/**
 * Returns rootId plus all descendant facility ids.
 */
export const expandFacilitySubtree = (
  facilities: ReadonlyArray<TFacilityTreeNode>,
  rootId: TUuid,
): TUuid[] => {
  const childrenByParent = buildFacilityChildrenMap(facilities);

  const result = new Set<TUuid>([rootId]);
  const queue = [rootId];
  while (queue.length) {
    const current = queue.shift()!;
    (childrenByParent.get(current) ?? []).forEach((child) => {
      if (!result.has(child.id)) {
        result.add(child.id);
        queue.push(child.id);
      }
    });
  }
  return [...result];
};

/**
 * DFS ordering used by planning rows:
 * descend children, then climb and visit remaining siblings.
 */
export const sortFacilityIdsDFS = (
  facilities: ReadonlyArray<TFacilityTreeNode>,
  startingPointIds: ReadonlyArray<TUuid>,
): TUuid[] => {
  const facilityMap: Record<TUuid, TFacilityTreeNode> = Object.fromEntries(
    facilities.map((facility) => [facility.id, facility]),
  );

  const childrenMap = buildFacilityChildrenMap(facilities);
  // Sibling order is display_order only (board behaviour). Unlike the frontend
  // DFS (which adds a name tie-break), this one deliberately omits it.
  childrenMap.forEach((children) => {
    children.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  });

  const visited = new Set<TUuid>();
  const result: TUuid[] = [];

  const dfs = (node?: TFacilityTreeNode) => {
    if (!node || visited.has(node.id)) return;
    visited.add(node.id);
    result.push(node.id);
    const children = childrenMap.get(node.id) ?? [];
    for (const child of children) {
      dfs(child);
    }
  };

  const goUpInHierarchy = (node?: TFacilityTreeNode) => {
    const parentId = node ? refId(node.parent_facility) : null;
    if (!parentId) return;
    const parent = facilityMap[parentId];
    const siblings = parent ? childrenMap.get(parent.id) ?? [] : [];
    for (const sibling of siblings) {
      if (!visited.has(sibling.id)) {
        dfs(sibling);
      }
    }
    goUpInHierarchy(parent);
  };

  for (const id of startingPointIds) {
    const node = facilityMap[id];
    dfs(node);
    goUpInHierarchy(node);
  }

  return result;
};

/**
 * Attaches a `children` array to root and every descendant (replacing any `children`
 * field T already carries), using an already-built parent→children map — for a batch
 * caller building many trees over the same facility list, buildFacilityChildrenMap then
 * only has to run once, not once per tree.
 *
 * visited guards against a parent_facility cycle in the data (see sortFacilityIdsDFS/
 * expandFacilitySubtree above) — only ever passed by a recursive call, never by a caller.
 */
export const attachFacilityChildren = <T extends TFacilityHierarchyRef>(
  childrenByParent: Map<TUuid, T[]>,
  root: T,
  visited: Set<TUuid> = new Set(),
): TWithChildren<T> => {
  visited.add(root.id);
  return {
    ...root,
    children: (childrenByParent.get(root.id) ?? [])
      .filter((child) => !visited.has(child.id))
      .map((child) => attachFacilityChildren(childrenByParent, child, visited)),
  };
};

export const findTreeNodeById = <T extends Readonly<{ id: TUuid; children: ReadonlyArray<T> }>>(
  startNode: T | null | undefined,
  id: TUuid,
): T | null => {
  if (!startNode?.id || !id) {
    return null;
  }
  if (startNode.id === id) {
    return startNode;
  }
  for (const childNode of startNode.children) {
    const node = findTreeNodeById(childNode, id);
    if (node) {
      return node;
    }
  }
  return null;
};

/**
 * Assembles a flat facility list (e.g. a single permission-scoped `readByQuery`
 * result) into a nested tree rooted at rootId. Returns null if rootId isn't present
 * in the input. Generic over T so both the facility-tree endpoint's narrow node shape
 * and the richer planning-groups one can share the same assembly logic.
 */
export const buildNestedFacilityTree = <T extends TFacilityHierarchyRef>(
  facilities: ReadonlyArray<T>,
  rootId: TUuid,
): TWithChildren<T> | null => {
  const root = facilities.find((facility) => facility.id === rootId);
  return root ? attachFacilityChildren(buildFacilityChildrenMap(facilities), root) : null;
};
